import React, { useState, useRef } from "react";
import { App } from "antd";
import {
  UploadOutlined, DeleteOutlined, MergeCellsOutlined,
  DownloadOutlined, FilePdfOutlined, HolderOutlined,
  PlusOutlined, CheckCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import { downloadAuthenticatedFile } from "../../utils/downloadFile";
import "./MergePdf.css";

const MergePdf = () => {
  const { message: msg } = App.useApp();
  const [files, setFiles]           = useState([]);   // { id, file, name }
  const [uploading, setUploading]   = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [dragOver, setDragOver]     = useState(false);
  const [dragging, setDragging]     = useState(null); // dragged item id
  const fileInputRef                = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  // ── File helpers ───────────────────────────────────────────────────
  const addFiles = (incoming) => {
    const pdfs = Array.from(incoming).filter((f) =>
      f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (pdfs.length === 0) { msg.error("Please select PDF files only."); return; }
    const mapped = pdfs.map((f) => ({ id: Date.now() + Math.random(), file: f, name: f.name }));
    setFiles((prev) => [...prev, ...mapped]);
    setDownloadUrl("");
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setDownloadUrl("");
  };

  const resetAll = () => { setFiles([]); setDownloadUrl(""); };

  const downloadMergedPdf = async () => {
    if (!downloadUrl || downloading) return;
    setDownloading(true);
    try {
      const authToken = localStorage.getItem("token") ?? "";
      await downloadAuthenticatedFile(downloadUrl, {
        token: authToken,
        fileName: "merged.pdf",
      });
    } catch {
      msg.error("Unable to download merged PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // ── Drag-and-drop file zone ────────────────────────────────────────
  const onZoneDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  // ── Drag-to-reorder list ───────────────────────────────────────────
  const onItemDragStart = (id) => setDragging(id);
  const onItemDragOver  = (e, overId) => {
    e.preventDefault();
    if (dragging === null || dragging === overId) return;
    setFiles((prev) => {
      const from = prev.findIndex((f) => f.id === dragging);
      const to   = prev.findIndex((f) => f.id === overId);
      const next = [...prev];
      next.splice(to, 0, next.splice(from, 1)[0]);
      return next;
    });
  };
  const onItemDragEnd = () => setDragging(null);

  // ── API call — mirrors Flutter exactly ────────────────────────────
  const handleMerge = async () => {
    if (files.length < 2) {
      msg.warning("Please select at least 2 PDF files to merge.");
      return;
    }

    setUploading(true);
    setDownloadUrl("");

    const authToken = localStorage.getItem("token") ?? "";
    const formData  = new FormData();
    formData.append("Source", isModule ? "Private-Web" : "Public-Web");
    files.forEach(({ file }) => formData.append("files", file));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/merge-pdf-files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!response.data.error && response.data.data?.downloadUrl) {
        setDownloadUrl(response.data.data.downloadUrl);
        msg.success(response.data.message || "PDFs merged successfully!");
      } else {
        msg.error(response.data.message || "Merge failed. Please try again.");
      }
    } catch (err) {
      msg.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`mg-root ${isModule ? "mg-module" : ""}`}>
        <div className="mg-inner">

          {/* Page title */}
          <div className="mg-header">
            <div className="mg-header-icon">
              <MergeCellsOutlined />
            </div>
            <div>
              <h1 className="mg-title">Merge PDF</h1>
              <p className="mg-subtitle">
                Join 2 or more PDF files into one single PDF file.
                Drag to reorder pages before merging.
              </p>
            </div>
          </div>

          {/* Upload zone */}
          {!downloadUrl && (
            <div
              className={`mg-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onZoneDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
              <div className="mg-dropzone-icon">
                <FilePdfOutlined />
              </div>
              <div className="mg-dropzone-text">
                {dragOver ? "Drop your PDFs here" : "Click to select or drag PDFs here"}
              </div>
              <div className="mg-dropzone-hint">Only PDF files · Multiple files supported</div>
              <button className="mg-choose-btn" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                <UploadOutlined /> Choose Files
              </button>
            </div>
          )}

          {/* File list */}
          {files.length > 0 && !downloadUrl && (
            <div className="mg-file-section">
              <div className="mg-file-section-head">
                <span className="mg-file-count">{files.length} file{files.length !== 1 ? "s" : ""} selected</span>
                <span className="mg-file-hint">
                  <HolderOutlined /> Drag rows to reorder
                </span>
                <button className="mg-add-more" onClick={() => fileInputRef.current?.click()}>
                  <PlusOutlined /> Add more
                </button>
              </div>

              <div className="mg-file-list">
                {files.map((item, index) => (
                  <div
                    key={item.id}
                    className={`mg-file-row ${dragging === item.id ? "dragging" : ""}`}
                    draggable
                    onDragStart={() => onItemDragStart(item.id)}
                    onDragOver={(e) => onItemDragOver(e, item.id)}
                    onDragEnd={onItemDragEnd}
                  >
                    <span className="mg-file-order">{index + 1}</span>
                    <HolderOutlined className="mg-drag-handle" />
                    <FilePdfOutlined className="mg-file-icon" />
                    <span className="mg-file-name">{item.name}</span>
                    <span className="mg-file-size">
                      {(item.file.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      className="mg-remove-btn"
                      onClick={() => removeFile(item.id)}
                      title="Remove"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success state */}
          {downloadUrl && (
            <div className="mg-success">
              <CheckCircleFilled className="mg-success-icon" />
              <div className="mg-success-title">Merge Complete!</div>
              <p className="mg-success-sub">
                Your {files.length} PDFs have been merged into one file.
              </p>
              <div className="mg-success-actions">
                <button
                  className="mg-download-btn"
                  onClick={downloadMergedPdf}
                  type="button"
                  disabled={downloading}
                >
                  {downloading ? <span className="mg-spinner" /> : <DownloadOutlined />}
                  {downloading ? "Downloading..." : "Download Merged PDF"}
                </button>
                <button className="mg-restart-btn" onClick={resetAll}>
                  Merge more files
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!downloadUrl && (
            <div className="mg-actions">
              <button
                className={`mg-merge-btn ${files.length < 2 || uploading ? "disabled" : ""}`}
                onClick={handleMerge}
                disabled={files.length < 2 || uploading}
              >
                {uploading ? (
                  <span className="mg-spinner" />
                ) : (
                  <MergeCellsOutlined />
                )}
                {uploading ? "Merging PDFs…" : "Merge PDFs"}
              </button>
              {files.length < 2 && files.length > 0 && (
                <p className="mg-warning">Add at least {2 - files.length} more PDF to merge</p>
              )}
            </div>
          )}

        </div>
      </div>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default MergePdf;
